// useValidation.ts
import { isInputField } from '../components/MpfForm/MpfForm.props';
import type { MpfFormBlockConfig, MpfFormFieldConfig } from '../components/MpfForm/MpfForm.props';
import { bloodPressureParts } from '../components/MpfInputBloodPressure/MpfInputBloodPressure.props';

export interface ValidationResult {
  errors: Record<string, string>;
  valid: boolean;
}

function validateEmail(value: string): boolean {
  if (!value) return true; // required handled separately
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
}

function validatePhone(value: string): boolean {
  if (!value) return true;
  const phoneRegex = /^[+\d\s().-]{7,20}$/;
  return phoneRegex.test(value);
}

function validatePostcode(value: string): boolean {
  if (!value) return true;
  const postcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]?\s*\d[ABD-HJLNP-UW-Z]{2})$/i;
  return postcodeRegex.test(value.trim());
}

function validateNumber(value: string): boolean {
  if (!value) return true;
  return /^-?\d+(\.\d+)?$/.test(value);
}

export function validateTextField(field: MpfFormFieldConfig, value: string): string {
  const trimmed = value?.trim?.() ?? '';

  if (field.required && !trimmed) {
    return 'This field is required.';
  }

  if (field.type === 'email' && trimmed && !validateEmail(trimmed)) {
    return 'Please enter a valid email address.';
  }

  if (field.type === 'phone' && trimmed && !validatePhone(trimmed)) {
    return 'Please enter a valid phone number.';
  }

  if (field.type === 'postcode' && trimmed && !validatePostcode(trimmed)) {
    return 'Please enter a valid postcode.';
  }

  if (field.type === 'number' && trimmed && !validateNumber(trimmed)) {
    return 'Please enter a number.';
  }

  return '';
}

/**
 * Both readings or neither. The component keeps "systolic/diastolic" as one string,
 * so a half-filled reading arrives here as "120/" or "/80".
 */
export function validateBloodPressureField(field: MpfFormFieldConfig, value: string): string {
  const { systolic, diastolic } = bloodPressureParts(value?.trim?.() ?? '');

  if (!systolic && !diastolic) {
    return field.required ? 'Enter a blood pressure reading.' : '';
  }

  if (!systolic || !diastolic) {
    return 'Enter both the systolic and diastolic readings.';
  }

  if (!validateNumber(systolic) || !validateNumber(diastolic)) {
    return 'Enter a blood pressure reading using numbers only.';
  }

  return '';
}

export function validateDateField(field: MpfFormFieldConfig, value: string): string {
  const dateStr = value?.trim?.() ?? '';

  if (!dateStr) {
    return field.required ? 'Please enter a valid date.' : '';
  }

  const parts = dateStr.split('-');
  if (parts.length !== 3) {
    return 'Enter a real date in the format YYYY-MM-DD.';
  }

  const [yyyy, mm, dd] = parts;
  const dateObj = new Date(`${yyyy}-${mm}-${dd}`);

  const validDate =
    dateObj instanceof Date &&
    !isNaN(dateObj.getTime()) &&
    dateObj.getUTCFullYear() === Number(yyyy) &&
    dateObj.getUTCMonth() + 1 === Number(mm) &&
    dateObj.getUTCDate() === Number(dd);

  if (!validDate) {
    return 'Enter a real date (for example, 1984-03-15).';
  }

  return '';
}

export function validateAll(
  fields: MpfFormBlockConfig[],
  values: Record<string, any>,
): ValidationResult {
  const errors: Record<string, string> = {};
  let valid = true;

  fields.filter(isInputField).forEach((field) => {
    const value = values[field.name];
    let error = '';

    // NB: Pluralised naming for groups of checkbox OR radio items
    // CHECKBOXES (array)
    if (field.type === 'checkboxes') {
      if (field.required && (!Array.isArray(value) || value.length === 0)) {
        error = 'Please select at least one option.';
      }
    } else if (field.type === 'radios' || field.type === 'select') {
      if (field.required && (value === null || value === undefined || value === '')) {
        error = 'Please choose an option.';
      }
    } else if (field.type === 'bloodPressure') {
      error = validateBloodPressureField(field, String(value ?? ''));
    } else if (field.type === 'date' || field.type === 'dateOfBirth') {
      error = validateDateField(field, String(value ?? ''));
    } else {
      error = validateTextField(field, String(value ?? ''));
    }

    errors[field.name] = error;
    if (error) valid = false;
  });

  return { errors, valid };
}

export function useValidation() {
  return {
    validateAll,
  };
}
